import { maxMarkdownBytes } from "@/src/lib/pdf/limits";

export const markdownFileExtensions = [".md", ".markdown", ".txt"] as const;

/** Comma-separated accept list for `<input type="file">`. */
export const markdownFileAccept = markdownFileExtensions.join(",");

export type ReadMarkdownFileSuccess = Readonly<{
  ok: true;
  markdown: string;
  /** Basename without extension, for the PDF filename field. */
  filename: string;
}>;

export type ReadMarkdownFileFailure = Readonly<{
  ok: false;
  error: string;
}>;

export type ReadMarkdownFileResult =
  | ReadMarkdownFileSuccess
  | ReadMarkdownFileFailure;

function getUtf8ByteLength(value: string) {
  return new TextEncoder().encode(value).length;
}

function getFileExtension(name: string) {
  const lastDot = name.lastIndexOf(".");
  if (lastDot <= 0 || lastDot === name.length - 1) {
    return "";
  }
  return name.slice(lastDot).toLowerCase();
}

export function getMarkdownFilenameStem(name: string) {
  const basename = name.replace(/^.*[\\/]/, "").trim();
  if (!basename) {
    return "document";
  }

  const extension = getFileExtension(basename);
  const stem =
    extension &&
    (markdownFileExtensions as readonly string[]).includes(extension)
      ? basename.slice(0, -extension.length)
      : basename;

  const normalized = stem.trim();
  return normalized.length > 0 ? normalized : "document";
}

export function isAllowedMarkdownFileName(name: string) {
  return (markdownFileExtensions as readonly string[]).includes(
    getFileExtension(name),
  );
}

/**
 * Read a local Markdown (or plain-text) file in the browser.
 * Does not upload to the server — returns UTF-8 text for the editor.
 */
export async function readMarkdownFile(
  file: File,
): Promise<ReadMarkdownFileResult> {
  if (!file || typeof file.name !== "string") {
    return { ok: false, error: "Choose a Markdown file to continue." };
  }

  if (!isAllowedMarkdownFileName(file.name)) {
    return {
      ok: false,
      error: `Use a ${markdownFileExtensions.join(", ")} file.`,
    };
  }

  if (file.size > maxMarkdownBytes) {
    return {
      ok: false,
      error: `File must be ${maxMarkdownBytes} bytes or less.`,
    };
  }

  let markdown: string;
  try {
    markdown = await file.text();
  } catch {
    return { ok: false, error: "Could not read that file." };
  }

  if (getUtf8ByteLength(markdown) > maxMarkdownBytes) {
    return {
      ok: false,
      error: `Markdown must be ${maxMarkdownBytes} bytes or less.`,
    };
  }

  if (markdown.trim().length === 0) {
    return { ok: false, error: "That file is empty." };
  }

  return {
    ok: true,
    markdown,
    filename: getMarkdownFilenameStem(file.name),
  };
}
